import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getMessageFromErrorResponse, isBaseError } from '@/api/v4/base-fetch';
import type { AddOrInviteTeamMemberPayload } from '@/api/v4/team.api';
import { addOrInviteTeamMember as addOrInviteTeamMemberAPI } from '@/api/v4/team.api';
import { QueryKey } from '@/config/query-client';
import { teamIdState } from '@/features/teams/team.state';
import { modalState } from '@/state/modal.state';
import { notify } from '@/ui/snackbar/notify';
import { useAtomValue, useSetAtom } from 'jotai';

export const useAddOrInviteTeamMember = () => {
  const { t } = useTranslation('default');

  const queryClient = useQueryClient();
  const teamId = useAtomValue(teamIdState);

  const setModalState = useSetAtom(modalState);

  const addOrInviteTeamMemberMutation = useMutation({
    mutationFn: addOrInviteTeamMemberAPI,
    onError: (err: Error) => {
      if (isBaseError(err)) {
        if (err.response.status === 422) {
          const message = getMessageFromErrorResponse(err);
          notify({ message: message ?? t`default:unknownError` });
          return;
        }
      }

      notify({ message: t`default:unknownError` });
      if (err instanceof Error) console.error(err.message);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKey.Team, teamId] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.TeamMembers] }),
      ]);
      setModalState(null);
    },
  });

  const addOrInviteTeamMember = async (payload: AddOrInviteTeamMemberPayload) =>
    await addOrInviteTeamMemberMutation.mutateAsync(payload);

  return {
    addOrInviteTeamMember,
    isLoading: addOrInviteTeamMemberMutation.isPending,
    isError: addOrInviteTeamMemberMutation.isError,
  };
};
